import { Frame, User } from 'core/types';
import { Category, RoleId } from 'core/types/role';
import { GroupUserPermissions, GroupFramePermissions } from './permissions';

export type GroupCategories = Exclude<Category, 'ORGANIZATION'>;
export type RoleForCategory = Record<GroupCategories, string>;
export type UserSubset = Pick<
  User,
  'id' | 'firstName' | 'lastName' | 'profilePicture'
>;
export type UserRoles = Record<GroupCategories, string>;

export interface SelectedUser {
  user: UserSubset;
  permissions: GroupUserPermissions;
  roles: RoleForCategory;
}

export function defaultRoles(allNone?: boolean): UserRoles {
  return {
    CALENDAR: 'none',
    CLOCK: 'none',
    GROUP: allNone ? 'none' : 'group:user',
    SHARE: 'none',
    VIDEOS: 'none',
    CONTENTPACKAGES: 'none',
  };
}

export function convertToRoleForCategory(roles: Role[]): RoleForCategory {
  return roles.reduce(
    (current, role) => ({
      ...current,
      [role.category]: role.id,
    }),
    defaultRoles(true),
  );
}

export interface Role {
  id: RoleId;
  name: string;
  category: GroupCategories;
}

export function isRole(data?: any): data is Role {
  if (!data || typeof data !== 'object') return false;
  return typeof data.id === 'string';
}

export type GroupUser = Pick<
  User,
  'id' | 'firstName' | 'lastName' | 'profilePicture'
>;

export interface UserWithRoles {
  user: GroupUser;
  roles: Role[];
  permissions: GroupUserPermissions;
}

export interface GroupFrame {
  frame: Pick<Frame, 'id' | 'serialNumber' | 'name'>;
  permissions: GroupFramePermissions;
}

export interface Group {
  id: string;
  name: string;
  description?: string;
  users: UserWithRoles[];
  frames: GroupFrame[];
  subGroups?: Group[];
  parentGroup?: Pick<Group, 'id' | 'name'>;
  noAccess?: boolean;
}

export function isSubGroup(data: any, level: number): data is Group {
  if (typeof data !== 'object') return false;
  if (typeof data.id !== 'string') return false;
  if (level >= 4) {
    return true;
  }
  if (typeof data.name !== 'string') return false;
  if (!Array.isArray(data.users)) return false;
  if (!Array.isArray(data.frames)) return false;
  if (
    !Array.isArray(data.subGroups) ||
    !data.subGroups.every((subGroup: any) => isSubGroup(subGroup, level + 1))
  )
    return false;

  return true;
}

export function isGroup(data: any): data is Group {
  if (typeof data !== 'object') return false;
  if (typeof data.id !== 'string') return false;
  if (typeof data.name !== 'string') return false;
  if (!Array.isArray(data.users)) return false;
  if (!Array.isArray(data.frames)) return false;
  if (
    !Array.isArray(data.subGroups) ||
    !data.subGroups.every((subGroup: any) => isSubGroup(subGroup, 0))
  )
    return false;
  return true;
}

export interface WeatherLocation {
  id: string;
  geonameId?: number;
  source?: string;
  latitude: number;
  longitude: number;
  elevation?: number;
  timezone: string;
  nameNb: string;
  nameEn: string;
  nameLocal: string;
  typeNb: string;
  typeEn: string;
  geonamesFeatureClass?: string;
  geonamesFeatureCode?: string;
  municipality?: string;
  county?: string;
  countryNb: string;
  countryEn: string;
  urlNb: string;
  urlEn: string;
  order: number;
}

export interface FrameWeather {
  locationId: string;
  location: WeatherLocation;
}
